import React, { memo, useEffect, useState, useMemo } from 'react'
import injectStyles from 'react-jss'
import {
  getMissionTypeSpecificProps,
  getMissionMarkers,
  getMissionPath,
} from './util'
import DisplayMap from '../../GMap/DisplayMap'
import {
  getMissionActivitiesLegacy,
  getMissionActivities,
} from '../../../Module/graphQL'
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator'
import { styles } from './styles'

const MissionMapWithoutStyles = memo(props => {
  const {
    mission: {
      id,
      checkInTime,
      checkInLocation,
      checkoutTime,
      checkoutLocation,
      location,
      boundary,
      activities: initialActivities,
    },
    shouldShowCurrentPosition,
    classes,
  } = props

  const [currentLocation, setCurrentLocation] = useState(null)
  const [activities, setActivities] = useState(initialActivities)
  const [isLoading, setIsLoading] = useState(true)

  const updateActivities = async () => {
    try {
      const { mission } = props
      if (!mission.userId || !mission.checkInTime) return
      let getLegacyRecords = () =>
        getMissionActivitiesLegacy(id).then(response => {
          if (response.status !== 200) {
            alert('Unable to get legacy mission activity records.')
            throw new Error('Unable to get legacy misison activity records.')
          }
          return response.json()
        })

      let getRecordsVariables = {
        missions: [mission.id],
      }
      let getRecords = () =>
        getMissionActivities(getRecordsVariables).then(response => {
          if (response.status !== 200) {
            alert('Unable to get TB mission activity records.')
            throw new Error('Unable to get TB mission activity records.')
          }
          return response.json()
        })

      let [legacyData, data] = await Promise.all([
        getLegacyRecords().then(result => result.data),
        getRecords(),
      ])
      let mapTrailblazerNodeToActivity = f => ({
        datetime: f.properties.timestamp,
        latitude: f.geometry.coordinates[1],
        longitude: f.geometry.coordinates[0],
      })
      // Request all other activities
      let formattedActivities = data.data.searchFeatures.collection.features.map(
        mapTrailblazerNodeToActivity,
      )
      while (data.data.searchFeatures.pageInfo.hasNextPage) {
        getRecordsVariables.after = data.data.searchFeatures.pageInfo.endCursor
        data = await getRecords(getRecordsVariables)
        formattedActivities = formattedActivities.concat(
          data.data.searchFeatures.collection.features.map(
            mapTrailblazerNodeToActivity,
          ),
        )
      }

      const formattedLegacyActivities = legacyData.mission.activities.edges.map(
        ({ node }) => ({
          datetime: node.dateTime,
          latitude: node.position.latitude,
          longitude: node.position.longitude,
        }),
      )
      var activities = formattedLegacyActivities.concat(formattedActivities)

      const sortedActivities = activities.sort(
        (activityA, activityB) =>
          new Date(activityA.datetime) - new Date(activityB.datetime),
      )

      setActivities(sortedActivities)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    updateActivities()
    const activityUpdateInterval = setInterval(updateActivities, 2 * 60000)
    return () => {
      clearInterval(activityUpdateInterval)
      setActivities([])
    }
  }, [])

  useEffect(() => {
    if (shouldShowCurrentPosition) {
      const getCurrentPositionError = err => {
        console.warn('MissionMap > Map > getCurrentPositionError', { err })
        setCurrentLocation(null)
      }
      if (navigator.geolocation) {
        const savePosition = ({ coords }) => {
          setCurrentLocation({
            lat: coords.latitude,
            lng: coords.longitude,
          })
        }
        navigator.geolocation.getCurrentPosition(
          savePosition,
          getCurrentPositionError,
          {
            enableHighAccuracy: true,
            maximumAge: 60000,
            timeout: 4000,
          },
        )
      } else {
        getCurrentPositionError()
      }
    }
  }, [])

  const onMapLoad = (google, { map, markers, paths: [polyline] }) => {
    if (map) {
      const bounds = new google.maps.LatLngBounds()

      if (markers && markers.length > 0) {
        markers.forEach(({ position }) => bounds.extend(position))
      }

      if (path && path.length > 0) {
        polyline.path.forEach(path =>
          bounds.extend({ lat: path.lat, lng: path.lng }),
        )
      }

      if (boundary && boundary.length > 0) {
        boundary.forEach(p =>
          bounds.extend({ lat: p.latitude, lng: p.longitude }),
        )
      }

      map.fitBounds(bounds)
    }
  }

  const markers = getMissionMarkers({
    checkInTime,
    checkInLocation,
    checkoutTime,
    checkoutLocation,
    location,
    activities,
    currentLocation,
  })

  const { center, pathColor, polygon } = getMissionTypeSpecificProps(
    props.mission,
  )

  const path = getMissionPath({
    activities,
    checkInLocation,
    checkInTime,
    checkoutLocation,
    checkoutTime,
    pathColor,
  })

  return useMemo(
    () => (
      <div className={classes.wrapper}>
        <DisplayMap
          onMapLoad={onMapLoad}
          center={center}
          markers={markers}
          polygons={[polygon]}
          paths={[path]}
        />
        {isLoading && (
          <div className={classes.loadingWrapper}>
            <LoadingIndicator color="inherit" />
          </div>
        )}
      </div>
    ),
    [onMapLoad, center, markers, polygon, path, isLoading],
  )
})

const MissionMap = injectStyles(styles)(MissionMapWithoutStyles)

export { MissionMap }



// WEBPACK FOOTER //
// ./src/Components/Missions/MissionMap/index.js